import {
  AssessmentQuestion,
  ScanProgress,
  WeightTargetStep,
} from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import { Platform, StyleSheet, Text } from 'react-native';
import { connect } from 'react-redux';
import { discardBodyScaleScan } from '../../BodyScale/actions';
import BodyscaleScanModal from '../../BodyScale/BodyscaleScanModal';
import {
  bodyscanAssessmentProgressSelector,
  bodyscanAssessmentquestionsAndAnswersSelector,
  getQuestionStep,
} from '../../BodyScale/selectors';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { isMobile } from '../../utils/ScreenSizes';
import { backButonPressed } from '../../WeightTarget/actions';
import { RemoveTargetModal } from '../../WeightTarget/RemoveTargetModal';
import { weightTargetStepSelector } from '../../WeightTarget/selectors';
import { WeightTargetModal } from '../../WeightTarget/WeightTargetModal';
import { toggleBodyScanModal } from '../redux';
import { default as BaseModal } from './BaseModal';
import { StepProgress } from './StepProgress';
import ContinueOnAppScreen from '../../BodyScale/ContinueOnApp';

interface IProps {
  bodyscanModalVisible: boolean;
  weightTargetModalVisible: boolean;
  removeTargetModalVisible: boolean;
  weightTargetStep: WeightTargetStep;
  bodyscanAssessmentProgress: ScanProgress;
  bodyAssessmentQuestionStep: number;
  bodyscanAssessmentquestionsAndAnswers: AssessmentQuestion[];
  closeWeightTargetModal: () => void;
  closeBodyscanModal: () => void;
  t: (key: string) => string;
  continueOnAppScreenVisible: boolean;
}

const BodyScaleAndWeightTargetModal = ({
  bodyscanModalVisible,
  weightTargetModalVisible,
  removeTargetModalVisible,
  weightTargetStep,
  bodyscanAssessmentProgress,
  bodyscanAssessmentquestionsAndAnswers,
  bodyAssessmentQuestionStep,
  closeBodyscanModal,
  closeWeightTargetModal,
  t,
  continueOnAppScreenVisible,
}: IProps) => {
  const overrideWeightTargetModalMargin = [
    WeightTargetStep.BodyWeightSummary,
    WeightTargetStep.BodyWeightScaleQuestion,
  ].includes(weightTargetStep);
  const getSubtitle = () => {
    if (bodyscanModalVisible) {
      if (bodyscanAssessmentProgress === ScanProgress.CaptureMetrics) {
        return (
          <Text style={styles.bodyAssessmentSubtitleTextStyle}>
            {t('bodyAssessmentSubtitle')}
          </Text>
        );
      }
    }
  };
  const getShowHeader = () => {
    return !weightTargetModalVisible;
  };
  const getTitle = () => {
    if (bodyscanModalVisible) {
      if (!bodyscanAssessmentProgress || ScanProgress.RemoveScale) {
        return;
      }
      if (
        !bodyscanAssessmentProgress ||
        bodyscanAssessmentProgress === ScanProgress.OverrideBodyType
      ) {
        return t('changeMyBodyMetrics');
      }
      if (
        bodyscanAssessmentProgress === ScanProgress.CaptureMetrics ||
        bodyscanAssessmentProgress === ScanProgress.Onboarding ||
        bodyscanAssessmentProgress === ScanProgress.AssessmentResults
      ) {
        return (
          <StepProgress
            totalSteps={bodyscanAssessmentquestionsAndAnswers.length}
            step={
              bodyscanAssessmentProgress === ScanProgress.CaptureMetrics ||
              bodyscanAssessmentProgress === ScanProgress.AssessmentResults
                ? bodyAssessmentQuestionStep
                : 1
            }
            progress={bodyscanAssessmentProgress}
            t={t}
          />
        );
      } else {
        return t('bodyScan');
      }
    }
  };

  const getUseSafeAreaView = () => {
    if (weightTargetModalVisible) {
      return !overrideWeightTargetModalMargin;
    }
  };

  const getCloseModal = () => {
    if (weightTargetModalVisible) {
      return closeWeightTargetModal;
    }
    return closeBodyscanModal;
  };
  const getHideCloseButton = () => {
    return weightTargetModalVisible;
  };

  const getBodyStyle = () => {
    if (weightTargetModalVisible) {
      return styles.weightTargetBodyStyle;
    }
  };

  const getHeaderTextStyle = () => {
    if (bodyscanModalVisible) {
      if (
        !bodyscanAssessmentProgress ||
        bodyscanAssessmentProgress === ScanProgress.BeforeScan
      ) {
        return;
      }
      if (
        bodyscanAssessmentProgress === ScanProgress.Onboarding ||
        bodyscanAssessmentProgress === ScanProgress.OverrideBodyType ||
        bodyscanAssessmentProgress === ScanProgress.AssessmentResults
      ) {
        return styles.bodyAssessmentHeaderTextStyle;
      }
      return bodyscanAssessmentProgress === ScanProgress.CaptureMetrics
        ? styles.bodyAssessmentHeaderTextStyle
        : styles.bodyScanHeaderTextStyle;
    }
  };

  return (
    <BaseModal
      isModalVisible={bodyscanModalVisible
        || weightTargetModalVisible
        || removeTargetModalVisible
        || continueOnAppScreenVisible
      }
      showHeader={getShowHeader()}
      title={getTitle()}
      subtitle={getSubtitle()}
      useSafeAreaView={getUseSafeAreaView()}
      hideCloseButton={getHideCloseButton()}
      bodyStyle={getBodyStyle()}
      containerStyle={
        isMobile && Platform.OS === 'web' ? { height: '100vh' } : null
      }
      closeModal={getCloseModal()}
      headerTextStyle={getHeaderTextStyle()}
    >
      {Platform.OS !== 'web' && bodyscanModalVisible && <BodyscaleScanModal />}
      {weightTargetModalVisible && <WeightTargetModal />}
      {removeTargetModalVisible && <RemoveTargetModal />}
      {continueOnAppScreenVisible && <ContinueOnAppScreen />}
    </BaseModal>
  );
};

const mapStateToProps = (state: any, ownProps) => {
  const {
    weightTargetModalVisible,
    bodyscanModalVisible,
    removeTargetModalVisible,
    continueOnAppScreenVisible,
  } = state.ui;

  return {
    weightTargetModalVisible,
    bodyscanModalVisible,
    removeTargetModalVisible,
    continueOnAppScreenVisible,
    weightTargetStep: weightTargetStepSelector(state),
    bodyscanAssessmentProgress: bodyscanAssessmentProgressSelector(state),
    bodyscanAssessmentquestionsAndAnswers:
      bodyscanAssessmentquestionsAndAnswersSelector(state),
    bodyAssessmentQuestionStep: getQuestionStep(state),
  };
};

const mapDispatchToProps = (dispatch: any, ownProps) => {
  const userProfile = ownProps?.user?.userProfile;

  return {
    closeWeightTargetModal: () => dispatch(backButonPressed()),
    closeBodyscanModal: () => {
      dispatch(discardBodyScaleScan());
      dispatch(toggleBodyScanModal(false));
    },
  };
};
export const BodyScaleAndWeightTargetModalContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  translate(['bodyscan'], { wait: true })(
    BodyScaleAndWeightTargetModal ,
  ),
);

const styles = StyleSheet.create({
  weightTargetBodyStyle: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  bodyAssessmentSubtitleTextStyle: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK_MEDIUM,
  },
  bodyAssessmentHeaderTextStyle: {
    ...Theme.textStyle.bodyCopyMed,
    zIndex: 1,
    paddingBottom: defaultScale(10),
    fontSize: defaultScale(20),
  },
  bodyScanHeaderTextStyle: {
    zIndex: 1,
    paddingBottom: defaultScale(10),
  },
});
